import {
  MATCH_MODE_IDS,
  MVP_FACTIONS,
  MAX_PLAYERS_PER_FACTION,
  RICHNESS_IDS,
  type FactionId,
} from '@sd/content';
import { LOCALES, t, type Locale } from '@sd/i18n';
import { BOT_LEVELS, PERSONALITY_IDS } from '@sd/bots';
import type { RoomListing, RoomSettings, RoomView, SeatControl } from '../game/net';
import { escapeHtml, seatColor } from './format';
import { CONTROL_SCHEMES, type ControlScheme } from './settings';
import { richnessHint } from './lobby';

/**
 * The main menu and the network lobby (#139). The menu leads to a match
 * against bots, to network rooms and back to the developer's site; a room
 * is set up by its host, who places people and bots in seats and teams.
 */
export interface MenuHandlers {
  single(): void;
  create(): void;
  find(): void;
  locale(next: Locale): void;
  /** The control scheme picked (#162). */
  controls(next: ControlScheme): void;
}

export function renderMainMenu(
  root: HTMLElement,
  locale: Locale,
  online: boolean,
  portal: string | null,
  on: MenuHandlers,
  scheme: ControlScheme = 'mouse',
) {
  const offline = online ? '' : `disabled title="${t(locale, 'menu.offline')}"`;
  const item = (action: string, key: string, extra = '') => `
    <button class="sd-btn sd-btn--large sd-menu__item${action === 'single' ? ' sd-btn--primary' : ''}" type="button" data-action="${action}" ${extra}>
      <span>${t(locale, `menu.${key}`)}</span><small>${t(locale, `menu.${key}.hint`)}</small>
    </button>`;
  root.innerHTML = `
    <section class="sd-panel sd-panel--strong sd-lobby sd-menu" aria-labelledby="menu-title">
      <header class="sd-lobby__head">
        <h1 id="menu-title">${t(locale, 'game.title')}</h1>
        <p class="sd-muted">${t(locale, 'game.tagline')}</p>
      </header>
      <nav class="sd-menu__list">
        ${item('single', 'single')}
        ${item('create', 'create', offline)}
        ${item('find', 'find', offline)}
      </nav>
      <div class="sd-menu__setting">
        <span class="sd-label" id="menu-controls">${t(locale, 'menu.controls')}</span>
        <div class="sd-segmented" role="group" aria-labelledby="menu-controls">
          ${CONTROL_SCHEMES.map(
            (c) =>
              `<button class="sd-btn" type="button" data-controls="${c}" aria-pressed="${c === scheme}" title="${t(locale, `menu.controls.${c}.hint`)}">${t(locale, `menu.controls.${c}`)}</button>`,
          ).join('')}
        </div>
        <small class="sd-muted">${t(locale, `menu.controls.${scheme}.hint`)}</small>
      </div>
      <footer class="sd-lobby__foot">
        <div class="sd-segmented" role="group" aria-label="${t(locale, 'ui.language')}">
          ${LOCALES.map(
            (l) =>
              `<button class="sd-btn" type="button" data-locale="${l}" aria-pressed="${l === locale}">${l.toUpperCase()}</button>`,
          ).join('')}
        </div>
        ${portal ? `<a class="sd-btn" href="${escapeHtml(portal)}" data-action="portal">${t(locale, 'menu.portal')}</a>` : ''}
      </footer>
    </section>`;
  root.onchange = null;
  root.onclick = (event) => {
    const button = (event.target as HTMLElement).closest('button');
    if (!button || button.disabled) return;
    const { action, locale: next, controls } = button.dataset;
    if (controls === 'mouse' || controls === 'touchpad') return on.controls(controls);
    if (action === 'single') on.single();
    else if (action === 'create') on.create();
    else if (action === 'find') on.find();
    else if (next) on.locale(next as Locale);
  };
}

export interface FindHandlers {
  create(name: string, visibility: 'public' | 'private'): void;
  join(code: string): void;
  back(): void;
}

/** Open rooms to join, a room by its code, or a new room. */
export function renderRoomList(
  root: HTMLElement,
  locale: Locale,
  rooms: readonly RoomListing[] | null,
  defaultName: string,
  error: string | null,
  on: FindHandlers,
) {
  const rows = rooms?.length
    ? rooms
        .map(
          (r) => `<li class="sd-room">
            <span class="sd-room__name"><b>${escapeHtml(r.name)}</b><small class="sd-muted">${t(locale, 'net.hostedBy', { name: escapeHtml(r.host) })}</small></span>
            <span class="sd-muted">${t(locale, 'net.places', { people: r.people, seats: r.seats, open: r.open })}</span>
            <button class="sd-btn" type="button" data-join="${r.id}" ${r.open ? '' : 'disabled'}>${t(locale, 'net.join')}</button>
          </li>`,
        )
        .join('')
    : `<li class="sd-muted">${t(locale, rooms ? 'net.noRooms' : 'net.loading')}</li>`;
  root.innerHTML = `
    <section class="sd-panel sd-panel--strong sd-lobby sd-net" aria-labelledby="net-title">
      <header class="sd-lobby__head"><h1 id="net-title">${t(locale, 'net.title')}</h1></header>
      ${error ? `<p class="sd-error" role="alert">${escapeHtml(error)}</p>` : ''}
      <form class="sd-field" data-form="create">
        <span class="sd-label">${t(locale, 'net.create')}</span>
        <div class="sd-buttons">
          <input class="sd-input" name="name" maxlength="40" value="${escapeHtml(defaultName)}" aria-label="${t(locale, 'net.roomName')}" />
          <select class="sd-select" name="visibility" aria-label="${t(locale, 'net.visibility')}">
            <option value="public">${t(locale, 'net.public')}</option>
            <option value="private">${t(locale, 'net.private')}</option>
          </select>
          <button class="sd-btn sd-btn--primary" type="submit">${t(locale, 'net.createButton')}</button>
        </div>
      </form>
      <form class="sd-field" data-form="code">
        <span class="sd-label">${t(locale, 'net.byCode')}</span>
        <div class="sd-buttons">
          <input class="sd-input" name="code" maxlength="8" placeholder="ABC123" spellcheck="false" autocomplete="off" />
          <button class="sd-btn" type="submit">${t(locale, 'net.join')}</button>
        </div>
      </form>
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'net.open')}</span>
        <ul class="sd-rooms">${rows}</ul>
      </div>
      <footer class="sd-lobby__foot">
        <button class="sd-btn" type="button" data-action="back">${t(locale, 'net.back')}</button>
      </footer>
    </section>`;
  root.onchange = null;
  root.onclick = (event) => {
    const button = (event.target as HTMLElement).closest('button');
    if (!button || button.disabled) return;
    if (button.dataset.action === 'back') on.back();
    else if (button.dataset.join) on.join(button.dataset.join);
  };
  root.onsubmit = (event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    if (form.dataset.form === 'create') {
      on.create(
        String(data.get('name') ?? ''),
        data.get('visibility') === 'private' ? 'private' : 'public',
      );
    } else {
      const code = String(data.get('code') ?? '')
        .trim()
        .toUpperCase();
      if (code) on.join(code);
    }
  };
}

export interface RoomHandlersUi {
  settings(patch: RoomSettings): void;
  sit(index: number): void;
  start(): void;
  leave(): void;
  copyLink(): void;
}

const FACTIONS = MVP_FACTIONS.map((f) => f.id);
const TEAMS = [1, 2, 3, 4] as const;

/** Why the host cannot start yet, if so. */
export function startBlocker(room: RoomView): 'open seats' | 'too many of one faction' | null {
  if (room.seats.some((s) => s.control === 'open')) return 'open seats';
  const counts = new Map<FactionId, number>();
  for (const s of room.seats) counts.set(s.faction, (counts.get(s.faction) ?? 0) + 1);
  return [...counts.values()].some((n) => n > MAX_PLAYERS_PER_FACTION)
    ? 'too many of one faction'
    : null;
}

/** A room before its match: seats, teams and the match's settings. */
export function renderRoom(
  root: HTMLElement,
  locale: Locale,
  room: RoomView,
  me: string,
  note: string | null,
  on: RoomHandlersUi,
) {
  const host = room.host === me;
  const options = <T extends string | number>(
    values: readonly T[],
    value: T | undefined,
    label: (v: T) => string,
  ) =>
    values
      .map(
        (v) =>
          `<option value="${v}" ${v === value ? 'selected' : ''}>${escapeHtml(label(v))}</option>`,
      )
      .join('');
  const who = (control: SeatControl, account?: string, online = false) =>
    control === 'human'
      ? `${escapeHtml(account ?? '')}${account === room.host ? ` <small class="sd-muted">${t(locale, 'net.hostMark')}</small>` : ''} <span class="sd-dot${online ? ' sd-dot--on' : ''}" title="${t(locale, online ? 'net.online' : 'net.offline')}"></span>`
      : control === 'open'
        ? `<span class="sd-muted">${t(locale, 'net.openSeat')}</span>`
        : t(locale, 'net.bot', { name: t(locale, `personality.${control}.name`) });
  const seats = room.seats
    .map((s, i) => {
      const mine = s.control === 'human' && s.account === me;
      const faction =
        host || mine
          ? `<select class="sd-select" name="faction-${i}" aria-label="${t(locale, 'ui.lobby.faction')}">${options(FACTIONS, s.faction, (f) => t(locale, `faction.${f}.name`))}</select>`
          : `<span>${t(locale, `faction.${s.faction}.name`)}</span>`;
      const team = host
        ? `<select class="sd-select" name="team-${i}" aria-label="${t(locale, 'ui.lobby.team')}">
            <option value="" ${s.team === undefined ? 'selected' : ''}>${t(locale, 'ui.lobby.noTeam')}</option>
            ${options(TEAMS, s.team as (typeof TEAMS)[number] | undefined, (n) => t(locale, 'ui.lobby.teamN', { n }))}
          </select>`
        : `<span class="sd-muted">${s.team ? t(locale, 'ui.lobby.teamN', { n: s.team }) : t(locale, 'ui.lobby.noTeam')}</span>`;
      const control =
        host && s.account !== room.host
          ? `<select class="sd-select" name="control-${i}" aria-label="${t(locale, 'net.seatFor')}">
              <option value="${s.control === 'human' ? 'human' : 'open'}" ${s.control === 'open' || s.control === 'human' ? 'selected' : ''}>${t(locale, s.control === 'human' ? 'net.person' : 'net.openSeat')}</option>
              ${options(PERSONALITY_IDS, s.control as (typeof PERSONALITY_IDS)[number], (p) => t(locale, 'net.bot', { name: t(locale, `personality.${p}.name`) }))}
            </select>`
          : s.control === 'open'
            ? `<button class="sd-btn" type="button" data-sit="${i}">${t(locale, 'net.sit')}</button>`
            : '<span></span>';
      return `<li class="sd-seat sd-seat--net${mine ? ' sd-seat--mine' : ''}" style="--seat:${seatColor(s.id)}">
        <span class="sd-emblem sd-emblem--sm"></span>
        <span class="sd-seat__name">${who(s.control, s.account, s.online)}</span>
        ${faction}${team}${control}
      </li>`;
    })
    .join('');
  const seated = new Set(room.seats.flatMap((s) => (s.account ? [s.account] : [])));
  const standing = room.members.filter((m) => !seated.has(m.name));
  const blocker = startBlocker(room);
  const segmented = <T extends string | number | boolean>(
    name: string,
    values: readonly T[],
    value: T,
    label: (v: T) => string,
  ) =>
    `<div class="sd-segmented" role="group">${values
      .map(
        (v) =>
          `<button class="sd-btn" type="button" data-set="${name}" data-value="${String(v)}" aria-pressed="${v === value}" ${host ? '' : 'disabled'}>${escapeHtml(label(v))}</button>`,
      )
      .join('')}</div>`;
  root.innerHTML = `
    <section class="sd-panel sd-panel--strong sd-lobby sd-net" aria-labelledby="room-title">
      <header class="sd-lobby__head">
        <h1 id="room-title">${escapeHtml(room.name)}</h1>
        <p class="sd-muted">${t(locale, room.visibility === 'public' ? 'net.publicRoom' : 'net.privateRoom')} · ${t(locale, 'net.code')} <b class="sd-code">${room.id}</b>
          <button class="sd-btn sd-btn--small" type="button" data-action="copy">${t(locale, 'net.copyLink')}</button></p>
      </header>
      ${note ? `<p class="sd-net__note" role="status">${escapeHtml(note)}</p>` : ''}
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'ui.lobby.players')}</span>
        ${segmented('players', [2, 3, 4], room.seats.length, String)}
      </div>
      <ol class="sd-seats">${seats}</ol>
      ${standing.length ? `<p class="sd-muted">${t(locale, 'net.standing', { names: standing.map((m) => escapeHtml(m.name)).join(', ') })}</p>` : ''}
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'ui.lobby.richness')}</span>
        <div class="sd-segmented" role="group">${RICHNESS_IDS.map(
          (r) =>
            `<button class="sd-btn" type="button" data-set="richness" data-value="${r}" aria-pressed="${r === room.richness}" title="${richnessHint(locale, r)}" ${host ? '' : 'disabled'}>${t(locale, `richness.${r}.name`)}</button>`,
        ).join('')}</div>
      </div>
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'ui.lobby.findChoice')}</span>
        ${segmented('findChoice', [true, false], room.findChoice, (v) => t(locale, v ? 'ui.lobby.findChoice.on' : 'ui.lobby.findChoice.off'))}
      </div>
      <div class="sd-field" title="${t(locale, 'ui.lobby.botLevelHint')}">
        <span class="sd-label">${t(locale, 'ui.lobby.botLevel')}</span>
        ${segmented('botLevel', BOT_LEVELS, room.botLevel, (l) => t(locale, `botLevel.${l}.name`))}
      </div>
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'ui.lobby.mode')}</span>
        ${segmented('mode', MATCH_MODE_IDS, room.mode, (m) => t(locale, `mode.${m}.name`))}
      </div>
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'net.visibility')}</span>
        ${segmented('visibility', ['public', 'private'] as const, room.visibility, (v) => t(locale, `net.${v}`))}
      </div>
      <footer class="sd-lobby__foot">
        <button class="sd-btn" type="button" data-action="leave">${t(locale, host ? 'net.close' : 'net.leave')}</button>
        ${
          host
            ? `<span class="sd-buttons">${blocker ? `<small class="sd-muted">${t(locale, `net.blocked.${blocker === 'open seats' ? 'open' : 'faction'}`, { count: MAX_PLAYERS_PER_FACTION })}</small>` : ''}
              <button class="sd-btn sd-btn--primary sd-btn--large" type="button" data-action="start" ${blocker ? 'disabled' : ''}>${t(locale, 'ui.lobby.start')}</button></span>`
            : `<small class="sd-muted">${t(locale, 'net.waitHost', { name: escapeHtml(room.host) })}</small>`
        }
      </footer>
    </section>`;
  root.onsubmit = null;
  root.onclick = (event) => {
    const button = (event.target as HTMLElement).closest('button');
    if (!button || button.disabled) return;
    const { action, sit, set, value } = button.dataset;
    if (action === 'leave') on.leave();
    else if (action === 'start') on.start();
    else if (action === 'copy') on.copyLink();
    else if (sit) on.sit(Number(sit));
    else if (set === 'players') on.settings({ players: Number(value) });
    else if (set === 'findChoice') on.settings({ findChoice: value === 'true' });
    else if (set && value) on.settings({ [set]: value } as RoomSettings);
  };
  root.onchange = (event) => {
    const field = event.target as HTMLSelectElement;
    const [kind, index] = field.name.split('-');
    const i = Number(index);
    const patch = room.seats.map(() => ({}));
    if (kind === 'faction') patch[i] = { faction: field.value as FactionId };
    else if (kind === 'team') patch[i] = { team: field.value ? Number(field.value) : null };
    else if (kind === 'control') {
      if (field.value === 'human') return;
      patch[i] = { control: field.value as SeatControl };
    } else return;
    on.settings({ seats: patch });
  };
}
